package songwritersnotebook.app.utils

import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import kotlinx.coroutines.await
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import dev.gitlive.firebase.database.database
import org.khronos.webgl.Int8Array
import kotlin.js.Promise

actual class NetworkUtils actual constructor() {
    private val deviceNetworkStatus: Flow<Boolean> = callbackFlow {
        val onlineHandler = { _: Any -> trySend(true); Unit }
        val offlineHandler = { _: Any -> trySend(false); Unit }

        window.addEventListener("online", onlineHandler)
        window.addEventListener("offline", offlineHandler)

        trySend(window.navigator.onLine)

        awaitClose {
            window.removeEventListener("online", onlineHandler)
            window.removeEventListener("offline", offlineHandler)
        }
    }.distinctUntilChanged()

    actual val networkStatus: Flow<Boolean> = deviceNetworkStatus.combine(
        flow {
            try {
                // Poll .info/connected from Realtime Database for true connection status
                emitAll(Firebase.database
                    .reference(".info/connected")
                    .valueEvents
                    .map { it.value<Boolean>() == true })
            } catch (e: Exception) {
                // Fallback to true if Firebase fails to initialize
                emit(true)
            }
        }.onStart { emit(true) }
    ) { deviceOnline, firebaseOnline ->
        deviceOnline && firebaseOnline
    }.distinctUntilChanged()

    actual fun isNetworkAvailable(): Boolean = window.navigator.onLine

    private suspend fun getFromCache(url: String): ByteArray? {
        val key = url
        return try {
            // Using a local variable 'key' and capturing it in the JS function scope
            val bytesPromise = js("""
                new Promise(function(resolve) {
                    try {
                        var request = indexedDB.open('audio_cache_db_v2', 1);
                        request.onupgradeneeded = function(e) { 
                            e.target.result.createObjectStore('audio_blobs'); 
                        };
                        request.onsuccess = function(e) {
                            try {
                                var db = e.target.result;
                                var trans = db.transaction('audio_blobs', 'readonly');
                                var store = trans.objectStore('audio_blobs');
                                // Use the captured 'key' variable
                                var getReq = store.get(key);
                                getReq.onsuccess = function() { resolve(getReq.result); };
                                getReq.onerror = function() { resolve(null); };
                            } catch (err) {
                                console.error('IndexedDB Get Success Error:', err);
                                resolve(null);
                            }
                        };
                        request.onerror = function() { resolve(null); };
                    } catch (err) {
                        console.error('IndexedDB Open Error:', err);
                        resolve(null);
                    }
                })
            """) as Promise<org.khronos.webgl.Int8Array?>
            
            val result = bytesPromise.await() ?: return null
            result.unsafeCast<ByteArray>()
        } catch (e: Exception) {
            println("NetworkUtils: Cache read error: $e")
            null
        }
    }

    private fun saveToCache(url: String, bytes: ByteArray) {
        val key = url
        try {
            val int8Array = bytes.unsafeCast<org.khronos.webgl.Int8Array>()
            
            js("""
                try {
                    var request = indexedDB.open('audio_cache_db_v2', 1);
                    request.onupgradeneeded = function(e) { 
                        e.target.result.createObjectStore('audio_blobs'); 
                    };
                    request.onsuccess = function(e) {
                        try {
                            var db = e.target.result;
                            var trans = db.transaction('audio_blobs', 'readwrite');
                            var store = trans.objectStore('audio_blobs');
                            // Use the captured 'key' and 'int8Array' variables
                            store.put(int8Array, key);
                        } catch (err) {
                            console.error('IndexedDB Put Error:', err);
                        }
                    };
                } catch (err) {
                    console.error('IndexedDB Save Error:', err);
                }
            """)
        } catch (e: Exception) {
            println("NetworkUtils: Failed to save to cache: $e")
        }
    }

    actual suspend fun fetchAuthenticated(url: String): ByteArray {
        println("DEBUG_AUDIO_JS: NetworkUtils.fetchAuthenticated called for url: $url")
        // 1. Check Cache First (only for static assets or recordings)
        val isCachable = url.contains(".mp3") || url.contains(".m4a") || url.contains(".webm")
        if (isCachable) {
            val cached = getFromCache(url)
            if (cached != null) {
                println("DEBUG_AUDIO_JS: Returning cached result for $url (${cached.size} bytes)")
                return cached
            }
        }

        // 2. Fetch from Network
        val options = js("{}")
        options.mode = "cors"
        
        if (url.contains("firebasestorage.googleapis.com")) {
            println("DEBUG_AUDIO_JS: URL is firebasestorage, appending Auth/AppCheck")
            try {
                val user = Firebase.auth.currentUser
                if (user != null) {
                    val token = user.getIdToken(false)
                    val headers = js("{}")
                    headers.Authorization = "Bearer " + token
                    val appCheckToken = songwritersnotebook.app.getAppCheckToken()
                    if (appCheckToken != null) {
                        headers["X-Firebase-AppCheck"] = appCheckToken
                    }
                    options.headers = headers
                } else {
                    println("DEBUG_AUDIO_JS: Warning - Firebase user is null, cannot append auth token")
                }
            } catch (e: Exception) {
                println("NetworkUtils: Error getting auth token for storage fetch: $e")
            }
        }

        println("NetworkUtils: Fetching $url ...")
        val response = try {
            window.fetch(url, options).await()
        } catch (e: Exception) {
            println("NetworkUtils: window.fetch FAILED for $url: $e")
            throw e
        }

        if (!response.ok) {
            println("NetworkUtils: Fetch FAILED for $url - Status: ${response.status} ${response.statusText}")
            throw Exception("Failed to fetch ($url): ${response.status} ${response.statusText}")
        }
        
        println("DEBUG_AUDIO_JS: Fetch successful for $url, status=${response.status}")
        val buffer = response.arrayBuffer().await()
        println("DEBUG_AUDIO_JS: arrayBuffer length=${buffer.byteLength}")
        val int8Array = org.khronos.webgl.Int8Array(buffer)
        val bytes = int8Array.unsafeCast<ByteArray>()
        println("DEBUG_AUDIO_JS: Returning ${bytes.size} bytes from fetchAuthenticated")

        // 3. Save to Cache
        if (isCachable) {
            saveToCache(url, bytes)
        }

        return bytes
    }
}
